<template>
  <div id="nx-composition-module-wizard">
    <v-stepper v-model="step"
               class="elevation-0 transparent">
      <v-stepper-header class="elevation-0">
        <v-stepper-step
          color="info"
          step="1"
          :complete="step > 1"
          editable
        >
          {{ $t('message.composition.wizard.moduleType') }}
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step
          color="info"
          step="2"
          :complete="valid"
        >
          {{ $t('message.composition.wizard.moduleData') }}
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1" class="py-0 px-1">

          <nx-composition-module-list
            v-if="groupedModules"
            :group="groupedModules"
            :modules="groupedModules.modules"
            :board-type="boardType"
            @click:module="selectType"
            @load:img-error="setPlaceholder"
          ></nx-composition-module-list>

          <nx-composition-module-list
            v-else
            :modules="modules"
            :board-type="boardType"
            @click:module="selectType"
            @load:img-error="setPlaceholder"
          ></nx-composition-module-list>

        </v-stepper-content>

        <v-stepper-content step="2" class="py-0 pl-3 pr-2 pb-5 nx-composition-module-editor-wrapper">
          <template v-if="moduleHasFields">
            <!-- Por compatibilidades con proyectos que tienen problemas al mostrar formularios en la columna
            de edición de scopes (como el diario), hacemos que si hay un único scope se muestre el form como siempre -->
            <template v-if="scopes.length > 1">
              <template v-for="(scope, index) in scopes">
                <nx-form-scope-card
                  v-if="scope.fieldsConfig.length > 0"
                  :key="index"
                  :title="scope.title"
                  :icon="scope.icon"
                  :fields="scope.fields"
                  :errors="[]"
                  :warnings="[]"
                  :scope="scope.scope"
                  :collapsable="isScoped"
                >
                  <nx-form-generator-scope
                    :config="scope"
                    :disabled="fetchingData"
                    :value="draft"
                    @form="updateFormFlags"
                    @input="updateField"
                    @request-population="handleRequestPopulation"
                    class="nx-form"
                    :ref="`form-${index}`"
                    sidebar
                  ></nx-form-generator-scope>
                </nx-form-scope-card>
              </template>
            </template>
            <template v-else>
              <nx-form-generator-scope
                :config="scopes[0]"
                :disabled="fetchingData"
                :value="draft"
                @form="updateFormFlags"
                @input="updateField"
                @request-population="handleRequestPopulation"
                class="nx-form"
                ref="form-0"
                sidebar
              ></nx-form-generator-scope>
            </template>
          </template>
          <v-layout column justify-center align-center v-else>
            <div class="d-block pt-5">
              <v-icon :size="32" class="nx-text--level-2">announcement</v-icon>
            </div>
            <p class="nx-text--level-2">
              {{ $t('message.composition.module.noEditableFields') }}
            </p>
          </v-layout>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <v-progress-linear
      :indeterminate="fetchingData"
      background-opacity="0"
      height="4"
    ></v-progress-linear>
  </div>


</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import NxFormGeneratorScope from '@/modules/form/FormScopeGenerator';
import api from '@/modules/composition/api';

import {
  moduleFieldTriggersFieldsConfigAlteration,
  moduleTypesThatAltersFieldsConfig,
} from '@/modules/composition/config';

import NxCompositionModuleList from '@/modules/composition/components/module/CompositionModuleList';
import StorableEditorComponent from '@/mixins/storable-editor-component';
import PopulateModuleFields from '@/modules/composition/mixins/populate-module-fields';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import NxFormScopeCard from '@/modules/form/components/FormScopeCard.vue';
import { isArray } from 'lodash';

export default {
  mixins: [StorableEditorComponent, PopulateModuleFields],
  components: { NxFormScopeCard, NxCompositionModuleList, NxFormGeneratorScope },
  props: {
    loading: {
      type: Boolean,
    },
    slotType: {
      type: String,
    },
    boardType: {
      type: String,
      default: null,
    },
    moduleParentModule: {
      type: Object,
    },
  },
  provide() {
    return {
      $moduleType: () => this.moduleType,
      $getParentEditor: () => this,
    };
  },
  data() {
    return {
      step: '1',
      localFieldsConfig: null,
      localScopedFieldsConfig: null,
      fetchingDataCount: 0,
    };
  },
  computed: {
    isScoped() {
      if (!this.moduleType) {
        return false;
      }
      return (this.config.scopedFieldsConfig || []).length > 0;
    },
    scopes() {
      if (!this.moduleType) {
        return [];
      }
      if (!this.isScoped) {
        return [{
          icon: null,
          scope: this.config.name,
          title: this.config.name,
          fieldsConfig: this.filterScopeFields(this.fieldsConfig),
        }];
      }

      let scopedFieldsConfig = this.config.scopedFieldsConfig;
      if (this.localScopedFieldsConfig !== null) {
        scopedFieldsConfig = this.localScopedFieldsConfig;
      }

      return cloneDeep(scopedFieldsConfig).map((c) => {
        c.fieldsConfig = this.filterScopeFields(c.fieldsConfig);
        return c;
      });
    },
    modules() {
      return this.getters.modules;
    },
    groupedModules() {
      return this.getters.groupedModules;
    },
    creatingAddress() {
      return this.getters.creatingAddress;
    },
    id() {
      return this.state.compositionId;
    },
    type() {
      return 'composition';
    },
    /**
     * Module configuration.
     * @type {{}}
     */
    config() {
      if (!this.moduleType) {
        return null;
      }
      if (!this.modules) {
        return null;
      }
      return this.modules.find(module => module.type === this.moduleType) || null;
    },

    draft() {
      return this.state.wizard.form;
    },

    moduleType() {
      return this.state.wizard.moduleType;
    },

    /**
     * Module fields' configuration.
     * @type {[]}
     */
    fieldsConfig() {
      if (moduleTypesThatAltersFieldsConfig.includes(this.moduleType)) {
        return this.localFieldsConfig || [];
      }
      if (this.localFieldsConfig !== null) {
        return this.localFieldsConfig;
      }
      if (this.config !== null) {
        return this.config.fieldsConfig || [];
      }
      return [];
    },

    /**
     * Returns whether the current module has any editable field.
     */
    moduleHasFields() {
      return this.config
        && Array.isArray(this.config.fieldsConfig)
        && this.config.fieldsConfig.length > 0;
    },

    fetchingData: {
      get() {
        return this.fetchingDataCount > 0;
      },
      set(value) {
        if (value) this.fetchingDataCount += 1;
        else this.fetchingDataCount = Math.max(this.fetchingDataCount - 1, 0);
      },
    },

    dialogTitle() {
      if (this.step !== '1' && this.config && this.config.name) {
        return this.$t('message.wizard.new.moduleOfType', { type: this.config.name });
      }
      return this.title;
    },

    valid() {
      return this.state.wizard.valid;
    },
  },
  methods: {
    /**
     * Module configuration whose fields have been filtered.
     * @type {{}}
     */
    filterScopeFields(scopeFields) {
      const filteredFieldNames = this.fieldsConfig.map(fc => fc.field);
      return scopeFields.filter(field => filteredFieldNames.includes(field.field));
    },

    /**
     * Inputs the current module fields' value.
     */
    create() {
      if (!this.valid) return;
      if (!this.loading) {
        this.$emit('input', this.moduleType, this.draft);
        this.reset();
      }
    },

    /**
     * Resets the module wizard.
     */
    reset() {
      this.step = '1';
      this.localFieldsConfig = null;
      this.dispatch('WIZARD_SET_FORM', {});
      this.dispatch('WIZARD_SET_MODULE_TYPE', false);
      this.dispatch('WIZARD_SET_VALIDITY', false);
    },

    /**
     * Selects the module type.
     * @param {string} moduleType.
     */
    selectType(moduleType) {
      if (this.moduleType !== moduleType) {
        this.dispatch('WIZARD_SET_MODULE_TYPE', moduleType);
        this.dispatch('WIZARD_SET_FORM', {});
        this.dispatch('WIZARD_SET_VALIDITY', !this.moduleHasFields);
      }
      if (!this.moduleHasFields) {
        // Do not open the second step if the selected module has not editable fields.
        this.create();
        return;
      }

      this.step = '2';
    },

    setPlaceholder(event) {
      event.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";
    },

    /**
     * Updates the given field's value.
     * If the fields triggers the module re-population, a request will be made to the server to ask
     * for the module fields' values.
     * Also, redraws the module fields if the updated field triggers it.
     * @param {string} field
     * @param {*}      value
     * @return {Promise<>}
     */
    async updateField(field, value) {
      const [moduleFields] = await Promise.all([
        this.getPopulatedFields(field, value),
        this.checkModuleConfig(field, value),
      ]);

      const filteredModuleFields = Object
        .keys(moduleFields)
        // Filter the fields to be populated by config
        // Ignore fields configured as "ignorePopulation"
        .filter((key) => {
          if (field === key) {
            return true;
          }
          const config = this.fieldConfigByName(key) || {};
          if (config.ignorePopulation === true) {
            return false;
          }
          return true;
        })
        .reduce((result, key) => {
          if (!isEqual(this.draft[key], moduleFields[key])) {
            result[key] = moduleFields[key];
          }
          return result;
        }, {});

      const form = cloneDeep(this.draft);
      Object.entries(filteredModuleFields).forEach(([key, val]) => {
        this.checkModuleConfig(key, val);
        Object.assign(form, { [key]: val });
      });

      await this.dispatch('WIZARD_SET_FORM', form);

      // Validate the form right after the population occurs,
      // so the wizard knows it is valid an the module can be created.
      this.validateForm();
    },

    /**
     * @type {{}}
     */
    fieldConfigByName(name) {
      return this.fieldsConfig.find(config => config.field === name) || null;
    },

    async handleRequestPopulation({ fromField, toField }) {
      const populatedFields = await this.getPopulatedFields(fromField, this.draft[fromField]);
      if (!populatedFields[toField]) {
        return;
      }

      const form = cloneDeep(this.draft);
      Object.assign(form, { [toField]: populatedFields[toField] });

      await this.dispatch('WIZARD_SET_FORM', form);

      // Validate the form right after the population occurs,
      // so the wizard knows it is valid an the module can be created.
      this.validateForm();
    },

    /**
     * Updates the module's validity.
     * @param {{}} flags
     */
    updateFormFlags(flags) {
      let isValid = true;
      Object.keys(this.$refs).forEach((key) => {
        if (key.includes('form-')) {
          let formRef = this.$refs[key];
          // When there's no scopes configured, formRef is directly an object
          if (isArray(formRef)) {
            if (formRef.length < 1) {
              return;
            }
            formRef = formRef[0];
          }
          if (formRef.form.invalid) {
            isValid = false;
          }
        }
      });

      this.dispatch('WIZARD_SET_VALIDITY', isValid);
      this.$emit('form', flags);
    },

    /**
     * Checks if the module's fields must be redrawn and, if so, requests the list of fields that
     * will be displayed and hides the rest.
     * @param {string} field
     * @param value
     * @return {Promise<*>}
     */
    async checkModuleConfig(field, value) {
      if (moduleFieldTriggersFieldsConfigAlteration(this.moduleType, field)) {
        return this.updateModuleConfig(field === 'template' ? value : this.draft.template);
      }
      return false;
    },

    /**
     * Updates the list of fields that will be displayed based on the current module's type,
     * template and the slot it is in.
     * @return {Promise<void>}
     */
    // eslint-disable-next-line
    updateModuleConfig: debounce(async function (template) {
      this.fetchingData = true;
      const config = await api.moduleGetConfig({
        boardType: this.boardType,
        moduleType: this.moduleType,
        slotType: this.slotType,
        parentModuleType: get(this.moduleParentModule, 'type', null),
        parentModuleTemplate: get(this.moduleParentModule, 'fields.template', null),
        template: template || 'default',
        compositionType: this.getters.type,
      });
      this.localFieldsConfig = config.fieldsConfig || null;
      this.localScopedFieldsConfig = config.scopedFieldsConfig || null;
      this.fetchingData = false;
      this.validateForm();
    }, 250, { maxWait: 1000 }),

    dismiss() {
      if (this.dismissable) this.$emit('dismiss');
      this.reset();
    },

    validateForm() {
      this.$nextTick(() => {
        Object.keys(this.$refs).forEach((key) => {
          if (key.includes('form-')) {
            let formRef = this.$refs[key];
            // When there's no scopes configured, formRef is directly an object
            if (isArray(formRef)) {
              if (formRef.length < 1) {
                return;
              }
              formRef = formRef[0];
            }
            formRef.validate();
          }
        });
      });
    },
  },
  watch: {
    async moduleType() {
      if (moduleTypesThatAltersFieldsConfig.includes(this.moduleType)) {
        await this.updateModuleConfig(this.draft.template);
      } else {
        this.localFieldsConfig = null;
      }
      if (this.moduleType === null) {
        this.step = '1';
      }
    },
    modules: {
      async handler(modules) {
        if (modules.length === 1) {
          this.selectType(modules[0].type);
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="stylus">
#nx-composition-module-wizard {
  margin-top: 16px;
}

.nx-composition-module-wizard-wrapper > .container,
.nx-composition-module-wizard-wrapper.container {
  position: relative

  > .v-progress-linear {
    position: absolute
    top: 112px
    left: 0
    width: 100%
    margin: 0
  }
}

.module-list {
  .v-list__tile__avatar {
    min-width: 70px
  }
  .v-list__tile__title {
    height: auto;
    white-space: initial;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
  }
}
</style>
